'use client';
import { type Direction } from '@hello-pangea/dnd';
import { AnimatePresence } from 'framer-motion';

import { type Primitive } from '@craft/lib/primitives';

import { type MuiThemeCallbackOrValue } from '../../../types/mui/sx';
import { type QuantitativeSize } from '../../../types/sizes';
import { mergeSx } from '../../../util/mui/sx';
import { evaluateMuiThemeCallbackOrValue } from '../../../util/mui/theme';
import { quantitativeSizeToString } from '../../../util/sizes';
import { AnimatedStack, type AnimatedStackProps } from '../../animation/AnimatedStack';
import * as types from '../types';

export interface GenericListItemsStackProps extends Omit<
  AnimatedStackProps,
  'animate' | 'direction' | 'exit' | 'gap' | 'initial' | 'layout' | 'transition'
> {
  readonly direction?: Direction;
  /**
   * Defines the gap between the items in the list, either provided as a string value or a callback
   * that returns a string value.
   *
   * Default: (t) => t.spacing(1)
   */
  readonly gap?: MuiThemeCallbackOrValue<number | string>;
  readonly hasHeader?: boolean;
  readonly hasToolbar?: boolean;
  readonly isCollapsed?: boolean;
  readonly itemHeight?: QuantitativeSize;
  readonly minItemHeight?: QuantitativeSize;
}

export const GenericListItemsStack = ({
  children,
  direction,
  gap = t => t.spacing(1),
  hasHeader,
  hasToolbar,
  isCollapsed,
  itemHeight,
  minItemHeight,
  ref,
  ...props
}: GenericListItemsStackProps) => {
  const ifCollapsable = <T extends Primitive | Record<string, unknown>>(
    value: ((collapsable: boolean) => T) | T,
  ): T | undefined =>
    typeof isCollapsed === 'boolean'
      ? typeof value === 'function'
        ? value(isCollapsed)
        : value
      : undefined;
  return (
    <AnimatePresence>
      {!isCollapsed && (
        <AnimatedStack
          {...props}
          animate={ifCollapsable(collapsed =>
            collapsed ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 },
          )}
          direction={types.DirectionMap[direction ?? 'vertical']}
          exit={ifCollapsable({
            height: 0,
            opacity: 0,
          })}
          initial={ifCollapsable(collapsed =>
            collapsed ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 },
          )}
          layout={ifCollapsable(true)}
          ref={ref}
          sx={mergeSx({ minHeight: 0, width: '100%' }, props.sx, muiTheme => ({
            '& .MuiDivider-root': {
              '&:last-child': {
                /* Fixes an issue where the 'droppable.placeholder' is treated as an additional
                     element, causing an extra divider to appear at the bottom of the items. */
                display: 'none',
              },
            },
            '& > .MuiDivider-root': {
              marginBottom: '0px !important',
              marginTop: '0px !important',
              marginY: '0px !important',
            },
            '& > .MuiMenuItem-root': {
              height: itemHeight ? quantitativeSizeToString(itemHeight, 'px') : undefined,
              minHeight: minItemHeight ? quantitativeSizeToString(minItemHeight, 'px') : undefined,
            },
            '& > .MuiMenuItem-root:first-of-type':
              hasHeader || hasToolbar ? undefined : { paddingTop: muiTheme.spacing(1.25) },
            '& > .MuiMenuItem-root:last-of-type': { paddingBottom: muiTheme.spacing(1.25) },
            gap: evaluateMuiThemeCallbackOrValue(gap, muiTheme),
            paddingY: 0,
          }))}
          transition={ifCollapsable({ duration: 0.2, type: 'tween' as const })}
        >
          {children}
        </AnimatedStack>
      )}
    </AnimatePresence>
  );
};
